/**
 * Browser-side analytics: loads GA4 (gtag.js) and the Meta Pixel under the
 * visitor's cookie consent, and sends ecommerce events built by ./ecommerce.
 *
 * Why gtag.js directly and not the GTM container (GTM-TP67WQ7): the published
 * container holds only a legacy Universal Analytics tag. Google bridges that
 * to GA4 automatically, which delivers page views, but the bridged GA4 tag
 * ignores page-level `gtag('event', …)` commands (verified 2026-09-29 by a
 * live probe: page_view arrived, view_item/purchase never did). Loading the
 * same GA4 tag (G-PB770ZVQB0) directly keeps the property, the `_ga` cookie
 * and the admin-side tag settings (enhanced measurement incl. client-side
 * navigation page views, referral exclusions, key events) — and makes our
 * events arrive. Do NOT load both: the bridge + a direct tag race each other.
 *
 * Consent Mode v2, "basic" flavour: nothing Google loads before the visitor
 * accepts Analytics; when it loads, the default (all denied) and the visitor's
 * actual choice are queued ahead of the tag, so advertising signals stay off
 * unless Marketing was accepted too.
 *
 * Every function is a no-op on the server and never throws.
 */

import { log } from '@/lib/logger';
import {
  GOOGLE_CONSENT_DENIED,
  getStoredConsent,
  toGoogleConsent,
  type CookieConsent,
} from '@/lib/cookie-consent';
import type { TrackPayload } from './ecommerce';

export const GA4_MEASUREMENT_ID = 'G-PB770ZVQB0';
export const META_PIXEL_ID = '997046568445544';

type CommandFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: CommandFn;
    fbq?: CommandFn;
    __dcGoogleLoaded?: boolean;
    __dcMetaLoaded?: boolean;
  }
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/** Load GA4 once, with Consent Mode state queued before the tag. */
export function loadGoogle(consent: CookieConsent): void {
  if (!isBrowser() || window.__dcGoogleLoaded) return;
  window.__dcGoogleLoaded = true;
  try {
    window.dataLayer = window.dataLayer || [];
    // gtag.js only processes real `arguments` objects, not arrays.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
    window.gtag('consent', 'default', { ...GOOGLE_CONSENT_DENIED });
    window.gtag('consent', 'update', toGoogleConsent(consent));
    window.gtag('set', 'ads_data_redaction', !consent.marketing);
    window.gtag('js', new Date());
    // `config` sends the page_view for the page GA4 loads on; later in-app
    // navigations are reported by trackPageView().
    window.gtag('config', GA4_MEASUREMENT_ID);
    lastLocation = window.location.href;

    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(s);
  } catch (err) {
    log.warn('analytics.google.load_failed', { err });
  }
}

// Previous page URL, sent as page_referrer on in-app navigations (the
// browser's document.referrer still points at the landing page's referrer).
let lastLocation: string | null = null;

/**
 * Report an in-app (client-side) navigation as a GA4 page_view.
 *
 * GA4's own "page changes based on browser history events" does NOT fire on
 * this site: Next.js wraps history.pushState and its router bypasses GA's hook
 * (verified 2026-09-29 on both localhost and the live site — only the landing
 * page view was ever sent). So navigations are reported explicitly. If GA's
 * history detection ever starts working, page views would double — re-probe
 * after Next.js / GA upgrades.
 */
export function trackPageView(): void {
  if (!isBrowser()) return;
  const consent = getStoredConsent();
  if (!consent?.analytics) return;
  if (!window.__dcGoogleLoaded) {
    // First load under this consent: `config` sends the page_view itself.
    applyConsent(consent);
    return;
  }
  try {
    const href = window.location.href;
    if (href === lastLocation) return;
    window.gtag?.('event', 'page_view', {
      page_location: href,
      page_title: document.title,
      ...(lastLocation ? { page_referrer: lastLocation } : {}),
    });
    lastLocation = href;
  } catch (err) {
    log.warn('analytics.page_view_failed', { err });
  }
}

/** Push a changed choice to an already-loaded GA4 (e.g. Marketing added later). */
export function updateGoogleConsent(consent: CookieConsent): void {
  if (!isBrowser() || !window.__dcGoogleLoaded || !window.gtag) return;
  try {
    window.gtag('consent', 'update', toGoogleConsent(consent));
    window.gtag('set', 'ads_data_redaction', !consent.marketing);
  } catch (err) {
    log.warn('analytics.google.consent_update_failed', { err });
  }
}

// Meta's official base snippet, verbatim apart from the ids. Kept as source
// text so its queueing semantics (fbq stub → fbevents.js) stay exactly
// Meta's. Meta tracks client-side navigations as PageView on its own.
const META_SNIPPET = `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${META_PIXEL_ID}');
fbq('track', 'PageView');`;

/** Load the Meta Pixel once (caller has checked Marketing consent). */
export function loadMeta(): void {
  if (!isBrowser() || window.__dcMetaLoaded) return;
  window.__dcMetaLoaded = true;
  try {
    const s = document.createElement('script');
    s.id = 'meta-pixel';
    s.text = META_SNIPPET;
    document.head.appendChild(s);
  } catch (err) {
    log.warn('analytics.meta.load_failed', { err });
  }
}

/** Load whatever the stored/given consent allows. Idempotent. */
export function applyConsent(consent: CookieConsent | null): void {
  if (!consent) return;
  if (consent.analytics) {
    if (window.__dcGoogleLoaded) updateGoogleConsent(consent);
    else loadGoogle(consent);
  }
  if (consent.marketing) loadMeta();
}

// Per-page guard for events that carry an id (purchases): a remount or a
// duplicate render must not send the same transaction twice.
const sentIds = new Set<string>();

/**
 * Send one funnel event. GA4 half only with Analytics consent, Meta half only
 * with Marketing consent; without consent the event is dropped, not queued.
 */
export function track(payload: TrackPayload | null | undefined): void {
  if (!payload || !isBrowser()) return;
  const consent = getStoredConsent();
  if (!consent) return;

  if (payload.eventId) {
    const key = `${payload.ga?.name ?? payload.meta?.name}:${payload.eventId}`;
    if (sentIds.has(key)) return;
    sentIds.add(key);
  }

  try {
    if (payload.ga && consent.analytics) {
      applyConsent(consent);
      window.gtag?.('event', payload.ga.name, payload.ga.params);
    }
    if (payload.meta && consent.marketing) {
      loadMeta();
      if (payload.eventId) {
        window.fbq?.('track', payload.meta.name, payload.meta.params, { eventID: payload.eventId });
      } else {
        window.fbq?.('track', payload.meta.name, payload.meta.params);
      }
    }
  } catch (err) {
    log.warn('analytics.track_failed', { err, event: payload.ga?.name ?? payload.meta?.name });
  }
}

// First-party cookies per consent category. Google Ads click ids (_gcl*)
// are advertising, so they go with Marketing, not Analytics.
const ANALYTICS_COOKIE = /^(_ga|_gid|_gat)/;
const MARKETING_COOKIE = /^(_gcl|_fbp|_fbc)/;

/**
 * Delete the first-party cookies of the categories just withdrawn (only
 * those — withdrawing Marketing must not reset the GA4 client id). They are
 * set on the registrable domain (".danielcastle.ro"), so every parent domain
 * of the current host is tried. The page reloads right after, which unloads
 * the scripts themselves.
 */
export function clearTrackingCookies(withdrawn: { analytics: boolean; marketing: boolean }): void {
  if (!isBrowser()) return;
  try {
    const names = document.cookie
      .split(';')
      .map((c) => c.split('=')[0]?.trim())
      .filter(
        (n): n is string =>
          !!n && ((withdrawn.analytics && ANALYTICS_COOKIE.test(n)) || (withdrawn.marketing && MARKETING_COOKIE.test(n))),
      );
    const labels = window.location.hostname.split('.');
    const domains = labels.map((_, i) => labels.slice(i).join('.')).filter((d) => d.includes('.') || d === 'localhost');
    const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    names.forEach((name) => {
      document.cookie = `${name}=; ${expire}`;
      domains.forEach((d) => {
        document.cookie = `${name}=; ${expire}; domain=${d}`;
        document.cookie = `${name}=; ${expire}; domain=.${d}`;
      });
    });
  } catch (err) {
    log.warn('analytics.clear_cookies_failed', { err });
  }
}
